import React, { useState, useEffect } from 'react';
import { Paper, Typography, TextField, Button, CircularProgress, Link } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import vendorAuthService from '@/service/auth.service/vendor.auth.service';
import ToastService from '@/service/toaster.service';
import { useDispatch } from 'react-redux';
import { setAuthInfo } from '@/redux/auth/auth.slice';

export default function VerifyEmailOtp() {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();


  // Get email from location or localStorage
  const emailFromState = location.state?.email;
  const storedEmail = localStorage.getItem('vendorEmail');
  const email = emailFromState || storedEmail;

  // Store email to localStorage if received from state
  useEffect(() => {
    if (emailFromState) {
      localStorage.setItem('vendorEmail', emailFromState);
    }
  }, [emailFromState]);

  // Countdown timer for resend
  useEffect(() => {
    let timer;
    if (!canResend && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [countdown, canResend]);

  const handleOtpChange = (e) => {
    const input = e.target.value;
    
    // Allow only digits and limit to 5 characters
    if (/^\d{0,5}$/.test(input)) {
      setOtp(input);
      setError('');
    } else {
      setError('OTP must be a 5-digit number');
    }
  };


  const handleOtpVerify = async (e) => {
    e.preventDefault();
    if (otp.trim().length !== 5) {
      setError('Please enter a valid 5-digit OTP.');
      return;
    }

    setLoading(true);

    try {
      const response = await vendorAuthService.verifyEmailOtp(email, otp);
      console.log('OTP verification response:', response);
      if (response.status) {
        const { access_token, data:vendor } = response.data; // adjust according to your backend response structure
      
        // 1. Set access token & role in localStorage
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('role', 'vendor');
      
        // 2. Set auth info in Redux
        dispatch(setAuthInfo({
          id: vendor._id,
          role: 'vendor',
          email: vendor.email,
          first_name: vendor.first_name,
          last_name: vendor.last_name,
          profile_picture: vendor.profile_picture,
          two_factor_enabled: vendor.two_factor_enabled,
        }));
      
        // 3. Navigate to upload profile
        localStorage.setItem('in_progress_onboarding',true);
        setSuccessMessage('OTP Verified Successfully!');
        navigate('/vendor/onboarding/upload-profile-image', {
          state: { email },
        });
        }
    } catch (err) {
      console.error('Error verifying OTP:', err);
      const errorMessage = err.response?.data?.data.error || 'Something went wrong. Please try again later.';
      setError(errorMessage);
      ToastService.error(errorMessage); 
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setCanResend(false);
    setCountdown(30);

    try {
      const response = await vendorAuthService.resendEmailOtp(email);
      if (response.status) {
        ToastService.success('OTP resent successfully!');
      } else {
        setError('Failed to resend OTP. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again later.');
    }
  };

  return (

    <Paper className="inner-card" elevation={5} sx={{ padding: 4, mt: 8, alignContent:"center" }}>
      <Typography variant="h5" color="#5E5ACF" fontWeight="bold">
        Enter OTP
      </Typography>

      <Typography className="textField" sx={{ mt: 2 }}>
        A one-time password has been sent to: <Typography component="span" sx={{ fontWeight: 'bold', color: '#6a00f4', fontSize: '12px' }}>{email}</Typography>.
      </Typography>

      <form onSubmit={handleOtpVerify}>
        <TextField
          fullWidth
          margin="normal"
          label="Enter 5-digit OTP"
          variant="outlined"
          value={otp}
          onChange={handleOtpChange}
          error={!!error}
          helperText={error}
        />
        {successMessage && (
          <Typography color="green" sx={{ fontSize: '0.875rem' }}>
            {successMessage}
          </Typography>
        )}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          className="btn-submit"
          sx={{ mt: 2, color: 'black', fontWeight: 'bold' }}
          disabled={loading}
        >
          Verify OTP
        </Button>
      </form>

      {/* Resend OTP */}
      {loading ? (
        <CircularProgress size={24} sx={{ color: 'black', mt: 2 }} />
      ) : (
        <>
          {!canResend ? (
            <Typography variant="body2" color="gray" sx={{ mt: 2 }}>
              You can resend OTP in {countdown} seconds
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mt: 2 }}>
              <Link
                href="#"
                onClick={handleResendOtp}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: '#5E5ACF',
                  fontWeight: 'bold',
                }}
              >
                Resend OTP
              </Link>
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}
